import React, { useEffect, useState } from 'react';
import {
  Popover, Box, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, CircularProgress, Chip, Stack,
} from '@mui/material';
import { Warehouse } from '@mui/icons-material';
import { type KitStockDto } from '../../../services/kitService';
import productoService, { type EquivalenciasUmDetalle } from '../../../services/productoService';
import { formatStockCantidad } from '../../../utils/formatters';
import {
  aplicarStockEnUmLinea,
  fetchStockPorDepositoArticulo,
  type StockPorDepositoRow,
} from '../../../utils/stockUmVenta';

interface Props {
  codigoArticulo: string;
  descripcion: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  tipoArticulo?: string | null;
  /** UM de la línea: el stock del depósito (en unidad base) se expresa en esta UM. */
  umLinea?: string | null;
}

const cellHeadSx = { fontWeight: 600, fontSize: '0.75rem', py: 0.5, whiteSpace: 'nowrap' as const };
const cellBodySx = { fontSize: '0.8rem', py: 0.5 };

const FacturaPOSStockPopover: React.FC<Props> = ({
  codigoArticulo,
  descripcion,
  anchorEl,
  onClose,
  tipoArticulo,
  umLinea,
}) => {
  const [rows, setRows] = useState<StockPorDepositoRow[]>([]);
  const [kitStock, setKitStock] = useState<KitStockDto | null>(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open || !codigoArticulo) return;
    let cancelled = false;

    const doFetch = async () => {
      setLoading(true);
      setRows([]);
      setKitStock(null);
      try {
        const result = await fetchStockPorDepositoArticulo(codigoArticulo, { tipoArticulo });
        if (cancelled || !result) return;
        if (result.kind === 'kit') {
          setKitStock(result.kit);
        } else {
          const um = String(umLinea ?? '').trim();
          let detalle: EquivalenciasUmDetalle | null = null;
          if (um) {
            try {
              detalle = await productoService.getEquivalenciasUmDetalle(codigoArticulo);
            } catch {
              detalle = null;
            }
          }
          if (cancelled) return;
          const conv = (n: number) => (um ? aplicarStockEnUmLinea(n, um, detalle) : n);
          setRows(
            result.rows.map((r) => ({
              ...r,
              cantidadDeposito: conv(r.cantidadDeposito),
              cantidadAsignada: conv(r.cantidadAsignada),
              disponibleDeposito: conv(r.disponibleDeposito),
              stockProduccion: conv(r.stockProduccion),
              stockPendienteOc: conv(r.stockPendienteOc),
              atp: conv(r.atp),
            })),
          );
        }
      } catch {
        if (!cancelled) {
          setRows([]);
          setKitStock(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    doFetch();
    return () => {
      cancelled = true;
    };
  }, [open, codigoArticulo, tipoArticulo, umLinea]);

  const totalDisponible = kitStock
    ? Number(kitStock.disponible) || 0
    : rows.reduce((acc, r) => acc + (r.disponibleDeposito ?? 0), 0);
  const umTxt = String(umLinea ?? '').trim();
  const fmt = (n: number) => formatStockCantidad(Number(n) || 0);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { p: 2, minWidth: 420, maxWidth: 640, borderRadius: 2 } }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <Warehouse fontSize="small" color="action" />
        <Box>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            {descripcion}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            {codigoArticulo}
            {umTxt ? ` · cantidades en ${umTxt}` : ''}
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : kitStock ? (
        <>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Stock del kit (por componentes)
          </Typography>
          <Table size="small" sx={{ mb: 1.5 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={cellHeadSx}>Componente</TableCell>
                <TableCell align="right" sx={cellHeadSx}>Disp.</TableCell>
                <TableCell align="right" sx={cellHeadSx}>Kits pos.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kitStock.componentes.map((c) => (
                <TableRow
                  key={c.codigoArticulo}
                  hover
                  sx={c.esLimitante ? { bgcolor: 'warning.light' } : undefined}
                >
                  <TableCell sx={cellBodySx}>
                    {c.descripcion || c.codigoArticulo}
                  </TableCell>
                  <TableCell align="right" sx={cellBodySx}>
                    {fmt(c.stockDisponible)}
                  </TableCell>
                  <TableCell align="right" sx={cellBodySx}>
                    {fmt(c.kitsPosibles)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box display="flex" justifyContent="flex-end">
            <Chip
              label={`Total disponible: ${fmt(totalDisponible)}${umTxt ? ` ${umTxt}` : ''}`}
              size="small"
              color={totalDisponible > 0 ? 'success' : 'error'}
              variant="outlined"
            />
          </Box>
        </>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Sin información de stock disponible
        </Typography>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto', mb: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={cellHeadSx}>Depósito</TableCell>
                  <TableCell align="right" sx={cellHeadSx}>Actual</TableCell>
                  <TableCell align="right" sx={cellHeadSx}>Asignado</TableCell>
                  <TableCell align="right" sx={{ ...cellHeadSx, color: 'success.main' }}>
                    Disponible
                  </TableCell>
                  <TableCell align="right" sx={cellHeadSx}>Prod.</TableCell>
                  <TableCell align="right" sx={cellHeadSx}>OC</TableCell>
                  <TableCell align="right" sx={cellHeadSx}>ATP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.nroUbicacion} hover>
                    <TableCell sx={cellBodySx}>
                      {r.nombreUbicacion || `Depósito ${r.nroUbicacion}`}
                    </TableCell>
                    <TableCell align="right" sx={cellBodySx}>
                      {fmt(r.cantidadDeposito)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellBodySx, color: 'text.secondary' }}>
                      {fmt(r.cantidadAsignada)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...cellBodySx,
                        fontWeight: 600,
                        color: r.disponibleDeposito > 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {fmt(r.disponibleDeposito)}
                    </TableCell>
                    <TableCell align="right" sx={cellBodySx}>
                      {fmt(r.stockProduccion)}
                    </TableCell>
                    <TableCell align="right" sx={cellBodySx}>
                      {fmt(r.stockPendienteOc)}
                    </TableCell>
                    <TableCell align="right" sx={cellBodySx}>
                      {fmt(r.atp)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Chip
              label={`Total disponible: ${fmt(totalDisponible)}${umTxt ? ` ${umTxt}` : ''}`}
              size="small"
              color={totalDisponible > 0 ? 'success' : 'error'}
              variant="outlined"
            />
          </Box>
        </>
      )}
    </Popover>
  );
};

export default FacturaPOSStockPopover;
